import React, { useState, useEffect } from 'react';
import { Nav, Collapse } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { MENU_PATHS } from '../../constants/MenuPaths';
import CustomDropdown from '../ui/dropdowns/CustomDropdown';

/**
 * ------------------------------------------------------------------
 * NavbarItem
 * Componente encargado de renderizar los elementos de navegación del menú.
 * Se adapta a vistas verticales (sidebar/móvil) y horizontales (desktop).
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES
 * 1. buildDropdownItems ............. Genera la estructura de items para el dropdown
 * 2. renderMobileSubmenu ............ Renderiza un item con submenú en modo vertical (acordeón)
 * 3. renderDesktopDropdown .......... Renderiza un item con submenú en modo desktop (dropdown)
 * 4. renderSimpleLink ............... Renderiza un enlace de navegación simple
 * 5. NavbarItem ..................... Componente principal
 * ------------------------------------------------------------------
 * PROPS
 * @param {boolean} vertical - Define si el menú se renderiza verticalmente (true) u horizontalmente (false).
 * @param {function} onClick - Función callback que se ejecuta al hacer click en un enlace.
 * ------------------------------------------------------------------
 */

function NavbarItem({ vertical = false, isOpen = true, onClick }) {
  // ------------------------------------------------------------------
  // Hooks & State
  // ------------------------------------------------------------------
  const [showIcon, setShowIcon] = useState(window.innerWidth < 1400);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setShowIcon(window.innerWidth > 1400);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to check if a link is active (handling sub-routes)
  // Moved up to be available for useEffect
  const isLinkActive = (url) => {
    if (!url) return false;
    if (url === '/' && location.pathname !== '/') return false; // Strict for home
    return location.pathname === url || location.pathname.startsWith(url + '/');
  };

  // Auto-expand submenu if active link is found inside
  // Runs on mount, on path change, AND when drawer opens (if vertical)
  useEffect(() => {
    if (vertical && isOpen) {
      const activeIndex = MENU_PATHS.findIndex(link =>
        link.submenu && link.submenu.some(sub => sub.enabled && isLinkActive(sub.url))
      );
      if (activeIndex !== -1) {
        setOpenSubmenu(activeIndex);
      }
    }
  }, [location.pathname, vertical, isOpen]);

  const navClass = vertical ? 'flex-column gap-2' : 'd-flex align-items-center gap-2';

  // ------------------------------------------------------------------
  // 1. buildDropdownItems
  // ------------------------------------------------------------------
  const buildDropdownItems = (submenu) =>
    submenu.map((sub) => {
      const isSubActive = isLinkActive(sub.url);

      return {
        label: (
          <>
            {sub.icon && <i className={`bi ${sub.icon} me-2`}></i>}
            {sub.label}
          </>
        ),
        onClick: e => {
          e.preventDefault();
          navigate(sub.url);
          if (onClick) onClick(e);
        },
        className: `nav-item-link ${isSubActive ? 'active' : ''}`,
      };
    });

  // ------------------------------------------------------------------
  // 2. renderMobileSubmenu
  // ------------------------------------------------------------------
  const renderMobileSubmenu = (link, idx, isMenuActive) => (
    <div key={link.label}>
      <div
        className={`nav-item-link ${isMenuActive ? 'active' : ''} d-flex align-items-center gap-2`}
        onClick={() => setOpenSubmenu(openSubmenu === idx ? null : idx)}
      >
        <i
          className={`bi ${link.icon} me-2 ${showIcon || vertical ? '' : 'd-none'}`}
        ></i>
        {link.label}
        <i
          className={`bi ms-auto navbar-item-icon ${openSubmenu === idx ? 'bi-chevron-up' : 'bi-chevron-down'}`}
        ></i>
      </div>
      <Collapse in={openSubmenu === idx}>
        <div>
          {link.submenu
            .filter(s => s.enabled)
            .map((sub) => {
              const isSubActive = isLinkActive(sub.url);
              return (
                <div
                  key={sub.label}
                  className={`nav-item-link ps-5 ${isSubActive ? 'active' : ''}`}
                  onClick={e => {
                    e.preventDefault();
                    navigate(sub.url);
                    if (onClick) onClick(e);
                  }}
                >
                  {sub.icon && <i className={`bi ${sub.icon} me-2`}></i>}
                  {sub.label}
                </div>
              );
            })}
        </div>
      </Collapse>
    </div>
  );

  // ------------------------------------------------------------------
  // 3. renderDesktopDropdown
  // ------------------------------------------------------------------
  const renderDesktopDropdown = (link, isMenuActive) => (
    <CustomDropdown
      key={link.label}
      label={
        <>
          <i
            className={`bi ${link.icon} me-2 ${showIcon || vertical ? '' : 'd-none'}`}
          ></i>
          {link.label}
        </>
      }
      icon={null}
      items={buildDropdownItems(link.submenu.filter(s => s.enabled))}
      toggleProps={{
        className: `nav-item-link ${isMenuActive ? 'active' : ''}`,
      }}
      menuProps={{
        popperConfig: { strategy: 'fixed' },
      }}
      align="end"
    />
  );

  // ------------------------------------------------------------------
  // 4. renderSimpleLink
  // ------------------------------------------------------------------
  const renderSimpleLink = (link, isActive) => (
    <Nav.Link
      as="span"
      key={link.label}
      onClick={e => {
        if (link.url) {
          e.preventDefault();
          navigate(link.url);
          if (onClick) onClick(e);
        }
      }}
      className={`nav-item-link ${isActive ? 'active' : ''} ${vertical ? '' : 'd-flex no-wrap gap-2'}`}
    >
      <i
        className={`bi ${link.icon} me-1 ${showIcon || vertical ? '' : 'd-none'}`}
      ></i>
      {link.label}
    </Nav.Link>
  );

  // ------------------------------------------------------------------
  // 5. Render Principal
  // ------------------------------------------------------------------
  return (
    <Nav className={navClass}>
      {MENU_PATHS.filter(l => l.enabled).map((link, idx) => {
        const isActive = link.url && location.pathname === link.url;
        const hasSubmenu = link.submenu && link.submenu.some(s => s.enabled);
        const isChildActive =
          hasSubmenu && link.submenu.some(sub => sub.enabled && location.pathname === sub.url);
        const isMenuActive = isActive || isChildActive;

        // Caso 1: Submenú en modo vertical (Mobile/Sidebar)
        if (hasSubmenu && vertical) {
          return renderMobileSubmenu(link, idx, isMenuActive);
        }

        // Caso 2: Submenú en modo desktop (Dropdown)
        if (hasSubmenu) {
          return renderDesktopDropdown(link, isMenuActive);
        }

        // Caso 3: Link simple
        return renderSimpleLink(link, isActive);
      })}
    </Nav>
  );
}

export default NavbarItem;